import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'phosphor-react';
import { toast } from 'sonner';
import {
  centralApi,
  IColunaKanbanPermissao,
  IDeptConfigWhatsapp,
} from '../../hooks/useCentralPermissoes';
import { AZUL, BarraSalvar, Cartao, Eyebrow, Interruptor, MONO, TEXTO, TEXTO_SUAVE } from './ui';

const DEPARTAMENTOS = [
  'Fiscal',
  'Fiscal Artistas',
  'Contabil',
  'Depto. Pessoal',
  'Societario',
  'Comercial',
  'Consultoria',
  'Atendimento',
];

const PADRAO_WA: IDeptConfigWhatsapp = {
  restrict_by_dept: false,
  blur_messages: true,
  extra_depts: [],
};

async function lerConfigWhatsapp(grupoId: number): Promise<IDeptConfigWhatsapp> {
  const waApi = (await import('../../services/whatsapp/waApi')).default;
  const res = await waApi.get(`/dept-config/${grupoId}`);
  const dados = res.data?.data ?? res.data;
  return { ...PADRAO_WA, ...(dados ?? {}) };
}

/** Ações das conversas ficam em "Telas e ações" (módulo WhatsApp); aqui, o que é do serviço de WhatsApp. */
export function GrupoWhatsapp({
  grupoId,
  grupoNome,
  onIrParaTelas,
}: {
  grupoId: number;
  grupoNome: string;
  onIrParaTelas: (busca: string) => void;
}) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<IDeptConfigWhatsapp>(PADRAO_WA);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['central', 'wa-dept', grupoId],
    queryFn: () => lerConfigWhatsapp(grupoId),
    retry: false,
  });
  useEffect(() => setConfig(data ?? PADRAO_WA), [data]);
  const base = data ?? PADRAO_WA;
  const mudou = JSON.stringify(config) !== JSON.stringify(base);

  const salvar = useMutation({
    mutationFn: async () => {
      const waApi = (await import('../../services/whatsapp/waApi')).default;
      await waApi.put(`/dept-config/${grupoId}`, config);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central', 'wa-dept', grupoId] });
      toast.success(`WhatsApp de ${grupoNome} salvo`);
    },
    onError: () => toast.error('O serviço de WhatsApp não respondeu; nada foi salvo'),
  });

  if (isLoading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner color={AZUL} />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="16px">
      <Cartao p="20px 22px" display="flex" flexDirection="column" gap="14px">
        <Box>
          <Text fontSize="16px" fontWeight={650} color={TEXTO}>
            WhatsApp
          </Text>
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Quais departamentos o grupo enxerga nas conversas.
          </Text>
        </Box>
        {isError && (
          <Text fontSize="12.5px" color="#92400E" bg="#FEFCE8" p="10px 12px" borderRadius="14px">
            O serviço de WhatsApp não respondeu. Os valores abaixo são o padrão, não o que está
            gravado.
          </Text>
        )}
        <Eyebrow>DEPARTAMENTOS VISÍVEIS</Eyebrow>
        <Flex align="center" gap="12px">
          <Interruptor
            ligado={config.restrict_by_dept}
            rotulo="Só o próprio departamento"
            onChange={() => setConfig(c => ({ ...c, restrict_by_dept: !c.restrict_by_dept }))}
          />
          <Text fontSize="13.5px">Só o próprio departamento, mais os extras abaixo</Text>
        </Flex>
        <Flex align="center" gap="12px">
          <Interruptor
            ligado={config.blur_messages}
            desabilitado={!config.restrict_by_dept}
            rotulo="Borrar em vez de esconder"
            onChange={() => setConfig(c => ({ ...c, blur_messages: !c.blur_messages }))}
          />
          <Text fontSize="13.5px">Borrar as mensagens em vez de esconder</Text>
        </Flex>
        <Flex wrap="wrap" gap="8px" opacity={config.restrict_by_dept ? 1 : 0.5}>
          {DEPARTAMENTOS.map(d => {
            const on = config.extra_depts.includes(d);
            return (
              <Box
                as="button"
                type="button"
                key={d}
                disabled={!config.restrict_by_dept}
                aria-pressed={on}
                onClick={() =>
                  setConfig(c => ({
                    ...c,
                    extra_depts: on ? c.extra_depts.filter(x => x !== d) : [...c.extra_depts, d],
                  }))
                }
                h="32px"
                px="12px"
                borderRadius="999px"
                fontSize="13px"
                fontWeight={600}
                bg={on ? '#DFF3FF' : 'rgba(255,255,255,.6)'}
                color={on ? '#075985' : '#475569'}
                boxShadow={on ? undefined : 'inset 0 0 0 1px #CBD5E1'}
              >
                {on ? '✓ ' : '+ '}
                {d}
              </Box>
            );
          })}
        </Flex>
        <Flex
          align="center"
          gap="10px"
          wrap="wrap"
          pt="4px"
          borderTop="1px solid rgba(15,23,42,.06)"
        >
          <Text fontSize="12.5px" color={TEXTO_SUAVE} flex="1 1 240px">
            Assumir, responder, transferir e as demais ações das conversas ficam em Telas e ações,
            no módulo WhatsApp.
          </Text>
          <Button
            size="sm"
            variant="ghost"
            rightIcon={<ArrowRight size={14} />}
            onClick={() => onIrParaTelas('whatsapp')}
          >
            Abrir ações do WhatsApp
          </Button>
        </Flex>
      </Cartao>
      <BarraSalvar
        texto={
          mudou
            ? `WhatsApp de ${grupoNome} alterado. Só é gravado ao salvar.`
            : 'Nenhuma alteração pendente.'
        }
        semMudanca={!mudou}
        salvando={salvar.isPending}
        onDesfazer={() => setConfig(base)}
        onSalvar={() => salvar.mutate()}
      />
    </Flex>
  );
}

const COLUNAS = [
  'PROPOSTA',
  'CONTRATO',
  'ENVIADO P/ ASS.',
  'ASSINADO',
  'AGUARDANDO NF',
  'NF EMITIDA',
  'PAGO',
];
const ACOES_COLUNA = [
  { chave: 'avancar_para', rotulo: 'Avançar para cá' },
  { chave: 'retroceder_para', rotulo: 'Voltar para cá' },
  { chave: 'criar_card', rotulo: 'Criar card aqui' },
];
/** O backend libera estes grupos em qualquer coluna (Admin, Diretoria, Desenvolvimento). */
const GRUPOS_LIVRES = [1, 3, 5];

export function GrupoKanbanEventos({
  grupoId,
  grupoNome,
  onIrParaTelas,
}: {
  grupoId: number;
  grupoNome: string;
  onIrParaTelas: (busca: string) => void;
}) {
  const queryClient = useQueryClient();
  const livre = GRUPOS_LIVRES.includes(grupoId);
  const { data, isLoading } = useQuery({
    queryKey: ['central', 'kanban-colunas', grupoId],
    queryFn: () => centralApi.colunasKanban(grupoId),
    enabled: !livre,
  });

  const base = useMemo(() => {
    const m: Record<string, boolean> = {};
    COLUNAS.forEach(c =>
      ACOES_COLUNA.forEach(a => {
        m[`${c}|${a.chave}`] = true;
      }),
    );
    (data ?? []).forEach(p => {
      m[`${p.coluna_titulo}|${p.acao}`] = p.permitido;
    });
    return m;
  }, [data]);
  const [estado, setEstado] = useState<Record<string, boolean>>({});
  useEffect(() => setEstado(base), [base]);
  const mudou = Object.keys(base).some(k => base[k] !== estado[k]);

  const salvar = useMutation({
    mutationFn: () => {
      const itens: IColunaKanbanPermissao[] = [];
      COLUNAS.forEach(coluna =>
        ACOES_COLUNA.forEach(a =>
          itens.push({
            grupo_id: grupoId,
            coluna_titulo: coluna,
            acao: a.chave,
            permitido: estado[`${coluna}|${a.chave}`] ?? true,
          }),
        ),
      );
      return centralApi.salvarColunasKanban(itens);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['central', 'kanban-colunas', grupoId] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-coluna-perms'] });
      toast.success(`Colunas do Kanban de Eventos de ${grupoNome} salvas`);
    },
    onError: () => toast.error('Não foi possível salvar as colunas do Kanban de Eventos'),
  });

  return (
    <Flex direction="column" gap="16px">
      <Cartao p={0} overflow="hidden">
        <Box p="20px 22px 14px">
          <Text fontSize="16px" fontWeight={650} color={TEXTO}>
            Kanban de Eventos
          </Text>
          <Text fontSize="13px" color={TEXTO_SUAVE}>
            Para quais colunas o grupo pode mover e onde pode criar cards. Supervisores do card não
            são barrados por estas regras.
          </Text>
        </Box>
        {livre && (
          <Text px="22px" pb="18px" fontSize="13px" color={TEXTO_SUAVE}>
            Este grupo move cards em qualquer coluna (regra fixa do backend para Administrador,
            Diretoria e Desenvolvimento).
          </Text>
        )}
        {!livre && isLoading && (
          <Flex justify="center" py={10}>
            <Spinner color={AZUL} />
          </Flex>
        )}
        {!livre && !isLoading && (
          <Box overflowX="auto">
            <Box as="table" w="100%" minW="560px" style={{ borderCollapse: 'collapse' }}>
              <Box as="thead">
                <Box
                  as="tr"
                  fontFamily={MONO}
                  fontSize="10px"
                  letterSpacing="1px"
                  color={TEXTO_SUAVE}
                >
                  <Box as="th" textAlign="left" p="10px 22px">
                    COLUNA
                  </Box>
                  {ACOES_COLUNA.map(a => (
                    <Box as="th" key={a.chave} textAlign="center" p="10px">
                      {a.rotulo.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {COLUNAS.map(coluna => (
                  <Box as="tr" key={coluna} borderTop="1px solid rgba(15,23,42,.05)">
                    <Box as="td" p="10px 22px" fontSize="13.5px" fontWeight={600}>
                      {coluna}
                    </Box>
                    {ACOES_COLUNA.map(a => {
                      const k = `${coluna}|${a.chave}`;
                      const on = estado[k] ?? true;
                      return (
                        <Box
                          as="td"
                          key={a.chave}
                          textAlign="center"
                          bg={on !== base[k] ? '#FEF3C7' : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            aria-label={`${a.rotulo} ${coluna}`}
                            onChange={() => setEstado(e => ({ ...e, [k]: !on }))}
                            style={{ width: 18, height: 18, accentColor: AZUL }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
        <Flex
          align="center"
          gap="10px"
          wrap="wrap"
          p="14px 22px"
          borderTop="1px solid rgba(15,23,42,.05)"
        >
          <Text fontSize="12.5px" color={TEXTO_SUAVE} flex="1 1 240px">
            Editar card sem vínculo, pular coluna e as demais ações do Kanban de Eventos ficam em
            Telas e ações, na linha “Kanban de Eventos”.
          </Text>
          <Button
            size="sm"
            variant="ghost"
            rightIcon={<ArrowRight size={14} />}
            onClick={() => onIrParaTelas('Kanban de Eventos')}
          >
            Abrir ações do Kanban de Eventos
          </Button>
        </Flex>
      </Cartao>
      {!livre && (
        <BarraSalvar
          texto={
            mudou
              ? `Colunas de ${grupoNome} alteradas. Só são gravadas ao salvar.`
              : 'Nenhuma alteração pendente.'
          }
          semMudanca={!mudou}
          salvando={salvar.isPending}
          onDesfazer={() => setEstado(base)}
          onSalvar={() => salvar.mutate()}
        />
      )}
    </Flex>
  );
}
